/**
 * Shared building blocks for Breadcrumb.
 *
 * BreadcrumbItem is the static `Breadcrumb.Item` subcomponent, and
 * BreadcrumbList produces the processed <ol> subtree (overflow block +
 * aria-current on the last item). Both the styled-components and Tailwind
 * paths render this exact same list — only the root <nav> wrapper differs — so
 * the child-processing logic lives here once and is never forked.
 */

import * as React from "react";
import Link from "@sproutsocial/seeds-react-link";
import type {
  TypeBreadcrumbProps,
  TypeBreadcrumbItemProps,
} from "./BreadcrumbTypes";

export const BreadcrumbItem = ({
  children,
  href,
  ...rest
}: TypeBreadcrumbItemProps) => {
  return (
    <li>
      <Link href={href} {...rest}>
        {children}
      </Link>
    </li>
  );
};

BreadcrumbItem.displayName = "Breadcrumb.Item";

export const BreadcrumbList = ({
  overflow,
  children,
}: Pick<TypeBreadcrumbProps, "overflow" | "children">) => {
  const listItems = React.Children.toArray(children);
  const lastItem = listItems[listItems.length - 1];
  return (
    <ol>
      {overflow && (
        <>
          {overflow.rootItem}
          <li>{overflow.menu}</li>
        </>
      )}
      {listItems.map((item, i) => {
        // @ts-notes - item is a childNode which can be a string or number but `cloneElement` requires props
        const itemElem =
          typeof item === "object" && "props" in item ? (
            item
          ) : (
            <span>item</span>
          );
        return React.cloneElement(itemElem, {
          ...itemElem.props,
          "aria-current": listItems[i] === lastItem ? "page" : undefined,
        });
      })}
    </ol>
  );
};
